import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store as StoreIcon, ExternalLink, Plus, Trash2, Save, GripVertical, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SectionBlock, Field, FieldGrid } from "./Channel";
import { useUnsaved } from "../DashboardShell";

/**
 * Store (Merch) — MystiCast does not sell products directly. This screen
 * lets a streamer curate a small showcase that links out to their existing
 * merch store (Shopify, Fourthwall, Printful, Redbubble, etc.). Viewers see
 * previews on the channel page and click through to buy elsewhere.
 */
const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY"];
const EMPTY = { title: "", external_url: "", image_url: "", price_display: "", currency: "USD" };

export default function Store() {
  const [items, setItems] = useState([]);
  const [shopUrl, setShopUrl] = useState("");
  const [initialShop, setInitialShop] = useState("");
  const [draft, setDraft] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.allSettled([
      api.get("/merch/mine").then((r) => setItems(r.data.items || [])),
      api.get("/streamer/merch/shop").then((r) => {
        const v = r.data?.shop_url || "";
        setShopUrl(v); setInitialShop(v);
      }),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const shopDirty = shopUrl !== initialShop;
  useUnsaved(shopDirty);

  const saveShopUrl = async () => {
    try {
      const { data } = await api.patch("/streamer/merch/shop", { shop_url: shopUrl.trim() || null });
      const v = data.shop_url || "";
      setShopUrl(v); setInitialShop(v);
      toast.success("Shop link saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const addItem = async () => {
    if (!draft.title.trim() || !draft.external_url.trim()) return toast.error("Title and product URL are required");
    setCreating(true);
    try {
      await api.post("/merch", draft);
      toast.success(`"${draft.title}" added`);
      setDraft(EMPTY);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add item"); }
    finally { setCreating(false); }
  };

  const patchItem = async (id, patch) => {
    try { await api.patch(`/merch/${id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const removeItem = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await api.delete(`/merch/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const move = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    setItems(reordered);
    try { await api.post("/merch/reorder", { order: reordered.map((i) => i.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="section-store">
      {/* Introduction */}
      <div className="term-card p-4 border-dashed">
        <div className="flex gap-3">
          <StoreIcon className="w-5 h-5 shrink-0 text-[color:var(--sc-pink)]" />
          <div>
            <div className="sc-heading text-sm font-bold">External merch showcase</div>
            <p className="text-xs text-zinc-400 mt-1">
              MystiCast doesn&apos;t sell physical goods (yet). Add product previews here and each one links out to
              your Shopify, Fourthwall, Printful, Redbubble, or any other store you already use. Viewers see a curated
              row on your channel page and buy through your existing storefront.
            </p>
          </div>
        </div>
      </div>

      {/* Full shop URL */}
      <SectionBlock icon={ExternalLink} title="Your Storefront" color="cyan">
        <Field label="Shop URL" hint="Shown as a 'Visit full shop →' button next to your merch strip">
          <input
            className="sc-input"
            value={shopUrl}
            onChange={(e) => setShopUrl(e.target.value)}
            placeholder="https://your-store.myshopify.com"
            data-testid="store-shop-url"
          />
        </Field>
        {shopDirty && (
          <div className="flex justify-end">
            <button onClick={saveShopUrl} className="sc-btn text-xs flex items-center gap-1.5" data-testid="store-shop-save">
              <Save className="w-3.5 h-3.5" /> Save shop URL
            </button>
          </div>
        )}
      </SectionBlock>

      {/* Add new item */}
      <SectionBlock icon={Plus} title="Add Product Preview" color="volt">
        <FieldGrid>
          <Field label="Product title">
            <input className="sc-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={80} placeholder="e.g. Neon Logo Tee" data-testid="store-draft-title" />
          </Field>
          <Field label="Price to display" hint="Optional — free-form, e.g. '$24' or '€19.99'">
            <input className="sc-input" value={draft.price_display} onChange={(e) => setDraft({ ...draft, price_display: e.target.value })} maxLength={24} data-testid="store-draft-price" />
          </Field>
          <Field label="Product URL" fullWidth>
            <input className="sc-input" value={draft.external_url} onChange={(e) => setDraft({ ...draft, external_url: e.target.value })} placeholder="https://your-store.com/products/tee" data-testid="store-draft-url" />
          </Field>
          <Field label="Image URL" hint="Hosted image (Shopify/Cloudinary/etc.)" fullWidth>
            <input className="sc-input" value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} placeholder="https://…" data-testid="store-draft-image" />
          </Field>
          <Field label="Currency">
            <select className="sc-input" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} data-testid="store-draft-currency">
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </FieldGrid>
        <button
          onClick={addItem}
          disabled={creating || !draft.title.trim() || !draft.external_url.trim()}
          className="sc-btn text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="store-add-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add product
        </button>
      </SectionBlock>

      {/* Existing items */}
      <SectionBlock icon={StoreIcon} title="Your Merch Showcase" color="pink">
        {loading ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-500" /></div>
        ) : items.length === 0 ? (
          <div className="text-center text-xs text-zinc-500 py-6">No merch items yet. Add your first one above.</div>
        ) : (
          <div className="space-y-2" data-testid="store-items-list">
            {items.map((it, idx) => (
              <div key={it.id} className="flex items-center gap-3 p-3 border border-white/10 rounded" data-testid={`store-item-${it.id}`}>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`store-item-up-${it.id}`}>▲</button>
                  <button onClick={() => move(idx, +1)} disabled={idx === items.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`store-item-down-${it.id}`}>▼</button>
                </div>
                <div className="w-14 h-14 rounded overflow-hidden border border-white/10 bg-black/30 grid place-items-center shrink-0">
                  {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-4 h-4 text-zinc-600" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="sc-heading text-sm font-bold truncate">{it.title}</div>
                  <div className="text-[11px] text-zinc-500 sc-mono truncate">{it.external_url}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{it.price_display ? `${it.price_display} ${it.currency}` : it.currency} · {it.is_active ? "visible" : "hidden"}</div>
                </div>
                <button
                  onClick={() => patchItem(it.id, { is_active: !it.is_active })}
                  className="text-[10px] sc-mono px-2 py-1 rounded border border-white/10 hover:border-[color:var(--sc-volt)] hover:text-[color:var(--sc-volt)]"
                  data-testid={`store-item-toggle-${it.id}`}
                >
                  {it.is_active ? "Hide" : "Show"}
                </button>
                <a href={it.external_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-white/5 text-zinc-500 hover:text-[color:var(--sc-cyan)]" title="Open">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  onClick={() => removeItem(it.id, it.title)}
                  className="p-2 rounded hover:bg-white/5 text-zinc-500 hover:text-[color:var(--sc-pink)]"
                  data-testid={`store-item-delete-${it.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </SectionBlock>
    </div>
  );
}
